import { useEffect } from 'react';
import { requestErrorNotification } from '@gonfalon/error-boundaries/requestErrorNotification';
import { RESTAPIError } from '@gonfalon/errors/RESTAPIError';
import type { ExperimentTimeSeriesResults } from '@gonfalon/experiments/types';
import { msg } from '@gonfalon/messages/msg';
import {
  getExperimentCredibleIntervalTimeseriesResults,
  type GetExperimentCredibleIntervalTimeseriesResultsQueryParams,
} from '@gonfalon/openapi/operations/getExperimentCredibleIntervalTimeseriesResults';
import { keepPreviousData, useQuery } from '@tanstack/react-query';

type ExperimentResultsQueryProps = {
  projectKey: string;
  environmentKey: string;
  experimentKey: string;
  iterationId: string;
  enabled?: boolean;
};

type UseExperimentCredibleIntervalTimeseriesResultsProps = ExperimentResultsQueryProps & {
  metricKey: string;
  options?: Omit<GetExperimentCredibleIntervalTimeseriesResultsQueryParams, 'iterationId'>;
};

const useExperimentQueryErrorHandler = (isError?: boolean) => {
  useEffect(() => {
    if (isError) {
      requestErrorNotification(msg('experiments.experimentResults.loadError'));
      return;
    }
  }, [isError]);
};

export function experimentCredibleIntervalTimeseriesKeyPrefix({
  projectKey,
  environmentKey,
  experimentKey,
}: {
  projectKey: string;
  environmentKey: string;
  experimentKey: string;
}) {
  return ['experimentCredibleIntervalTimeseries', projectKey, environmentKey, experimentKey] as const;
}

/**
 * Hook fetches a timeseries of experiment results for a single metric
 */
export function useExperimentCredibleIntervalTimeseriesResults({
  metricKey,
  options,
  projectKey,
  environmentKey,
  experimentKey,
  iterationId,
  enabled = true,
}: UseExperimentCredibleIntervalTimeseriesResultsProps) {
  const query = useQuery<ExperimentTimeSeriesResults, RESTAPIError>({
    staleTime: 60 * 1000,
    placeholderData: enabled ? keepPreviousData : undefined,
    enabled: enabled && Boolean(experimentKey) && Boolean(metricKey) && Boolean(iterationId),
    queryKey: [
      ...experimentCredibleIntervalTimeseriesKeyPrefix({ projectKey, environmentKey, experimentKey }),
      metricKey,
      iterationId,
      options,
    ],
    queryFn: async ({ signal }) => {
      const { data, error, response } = await getExperimentCredibleIntervalTimeseriesResults(
        {
          projectKey,
          environmentKey,
          experimentKey,
          metricKey,
          query: { iterationId, ...options },
        },
        { signal },
      );
      if (!response.ok || error) {
        const errorBody = error as { code?: string; message?: string } | undefined;
        throw new RESTAPIError({
          status: response.status,
          url: response.url,
          code: errorBody?.code ?? 'unknown',
          message: errorBody?.message ?? `HTTP ${response.status}`,
        });
      }
      return data as ExperimentTimeSeriesResults;
    },
    retry: (failureCount: number, error: RESTAPIError) => {
      if (failureCount > 3) {
        return false;
      }
      return error.status !== 400;
    },
  });
  useExperimentQueryErrorHandler(query.isError);
  return query;
}
